import { useMemo } from 'react';
import semver from 'semver';
import { Cell, Column, Row, TableBody, TableHeader } from 'react-stately';

import { useUsersById } from '../../../store/users/useUsers.ts';
import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApi } from '../../../store/SelectedHomeyStore.js';

import { SettingsTable } from './SettingsTable';
import { FamilySettingsTableUsersCell } from './FamilySettingsTableUsersCell';
import { FamilySettingsTableUsersColumnHeader } from './FamilySettingsTableUsersColumnHeader';

export function FamilySettingsTableUsers(props) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const users = useUsersById();

  const hasLocalUserFeature = useMemo(() => {
    const version = semver.coerce(api?.homey.softwareVersion);

    if (import.meta.env.MODE === 'self-hosted') {
      return true;
    }

    if (version == null) {
      return false;
    }

    const hasLocalUserFeatureVersion = semver.satisfies(version, '>=12.8.0');
    const hasLocalUserFeature =
      api?.isCloud === false && api?.homey.platformVersion >= 2 && hasLocalUserFeatureVersion;

    return hasLocalUserFeature;
  }, [api]);

  const columns = [
    { key: 'name', name: i18n.messageFormatter('settings.system.usersColumnLabelName') },
    { key: 'role', name: i18n.messageFormatter('settings.system.usersColumnLabelRole') },
    hasLocalUserFeature
      ? {
          key: 'localUsername',
          name: i18n.messageFormatter('settings.system.usersColumnLabelLocalUsername'),
        }
      : undefined,
    { key: 'enabled', name: i18n.messageFormatter('settings.system.usersColumnLabelEnabled') },
    { key: 'actions', name: 'Actions' },
  ].filter(Boolean);

  const rows = useMemo(() => {
    const rows = [];
    const rolesOrder = ['owner', 'manager', 'user', 'guest'];

    for (const [, user] of Object.entries(users.byId ?? {})) {
      if (user.athomId != null) {
        rows.push(user);
      }
    }

    rows.sort((a, b) => {
      const roleDiff = rolesOrder.indexOf(a.role) - rolesOrder.indexOf(b.role);

      // If the roles are the same sort by name.
      return roleDiff !== 0 ? roleDiff : i18n.collator.compare(a.name, b.name);
    });

    return rows;
  }, [users.byId, i18n]);

  function renderCell() {
    return <FamilySettingsTableUsersCell />;
  }

  function renderColumnHeader() {
    return <FamilySettingsTableUsersColumnHeader />;
  }

  return (
    <SettingsTable
      aria-label="Table"
      selectionMode="none"
      renderCell={renderCell}
      renderColumnHeader={renderColumnHeader}
    >
      <TableHeader columns={columns}>
        {(column) => {
          return <Column>{column.name}</Column>;
        }}
      </TableHeader>

      <TableBody items={rows}>
        {(item) => {
          return (
            <Row>
              {(columnKey) => {
                switch (columnKey) {
                  default:
                    return (
                      <Cell
                        value={item[columnKey]}
                        item={item}
                        textValue={String(item[columnKey] ?? '-')}
                      />
                    );
                }
              }}
            </Row>
          );
        }}
      </TableBody>
    </SettingsTable>
  );
}
